function defaultGetUpGrowth(lane) {
    return lane.data?.upGrowth ?? 0;
}
function defaultUpdateLaneData(lane, patch) {
    const data = lane.data;
    return {
        ...lane,
        data: {
            ...data,
            width: patch.width,
            height: patch.height,
            upGrowth: patch.upGrowth
        },
        style: {
            ...lane.style,
            width: patch.width,
            height: patch.height
        }
    };
}
/**
 * A lane child's base coordinates. The lane node itself carries the growth
 * offsets: its `position.x` is `-leftGrowth`, so the child's absolute X
 * (`parent.x + rf.x`) IS its baseX; on the y-axis the lane data's `upGrowth`
 * is subtracted from the child's relative y.
 */ export function childBasePosition(child, parent, getUpGrowth = defaultGetUpGrowth) {
    return {
        x: parent.position.x + child.position.x,
        y: child.position.y - getUpGrowth(parent)
    };
}
function collectLanes(nodes, isLane) {
    const laneIds = [];
    const laneById = new Map();
    for (const n of nodes){
        if (!isLane(n)) continue;
        laneIds.push(n.id);
        laneById.set(n.id, n);
    }
    return {
        laneIds,
        laneById
    };
}
function collectLaneChildren(nodes, laneById, isLane, getNodeDimensions, getUpGrowth) {
    const children = [];
    for (const n of nodes){
        if (!n.parentId || isLane(n)) continue;
        const parent = laneById.get(n.parentId);
        if (!parent) continue;
        const { x: baseX, y: baseY } = childBasePosition(n, parent, getUpGrowth);
        const { width, height } = getNodeDimensions(n);
        children.push({
            id: n.id,
            laneId: n.parentId,
            baseX,
            baseY,
            width,
            height
        });
    }
    return children;
}
/**
 * The pure core of the lane layout: given lane order and child base
 * coordinates, derive every lane's upGrowth, height, width, and stacked Y
 * (including the shift pass where a grown lane pushes the lanes above it up).
 */ function computeLaneStack(laneIds, children, metrics) {
    const minChildX = metrics.labelSize + metrics.paddingX;
    const accum = new Map();
    let globalMinBaseX = Infinity;
    for (const c of children){
        let a = accum.get(c.laneId);
        if (!a) {
            a = {
                minBaseY: Infinity,
                maxRight: 0,
                maxBottom: 0
            };
            accum.set(c.laneId, a);
        }
        if (c.baseY < a.minBaseY) a.minBaseY = c.baseY;
        if (c.baseX < globalMinBaseX) globalMinBaseX = c.baseX;
        const right = c.baseX + c.width;
        if (right > a.maxRight) a.maxRight = right;
        const bottom = c.baseY + c.height;
        if (bottom > a.maxBottom) a.maxBottom = bottom;
    }
    const leftGrowth = children.length ? Math.max(0, minChildX - globalMinBaseX) : 0;
    const laneMetrics = new Map();
    let uniformWidth = minChildX;
    for (const id of laneIds){
        const a = accum.get(id);
        const m = a ? {
            upGrowth: Math.max(0, metrics.paddingY - a.minBaseY),
            height: 0,
            // Always ≥ minChildX + paddingX: node widths are non-negative, so
            // maxRight ≥ this lane's min baseX ≥ globalMinBaseX, and the
            // leftGrowth definition tops the sum up to at least minChildX.
            laneWidth: a.maxRight + leftGrowth + metrics.paddingX
        } : {
            upGrowth: 0,
            height: metrics.minSize,
            laneWidth: minChildX
        };
        if (a) {
            m.height = Math.max(metrics.minSize, m.upGrowth + a.maxBottom + metrics.paddingY);
        }
        laneMetrics.set(id, m);
        if (m.laneWidth > uniformWidth) uniformWidth = m.laneWidth;
    }
    // Lane Y positions with upGrowth cascade.
    const laneY = new Map();
    let prevLaneY = 0;
    let prevHeight = 0;
    for(let i = 0; i < laneIds.length; i++){
        const m = laneMetrics.get(laneIds[i]);
        // `-0` propagates into later arithmetic; normalize to +0.
        const y = i === 0 ? m.upGrowth === 0 ? 0 : -m.upGrowth : prevLaneY + prevHeight + metrics.gap - m.upGrowth;
        laneY.set(laneIds[i], y);
        prevLaneY = y;
        prevHeight = m.height;
    }
    for(let i = 1; i < laneIds.length; i++){
        const prevM = laneMetrics.get(laneIds[i - 1]);
        const currentY = laneY.get(laneIds[i]);
        const prevY = laneY.get(laneIds[i - 1]);
        const shift = prevY + prevM.height - (currentY - metrics.gap);
        if (shift > 0) {
            for(let j = 0; j < i; j++){
                laneY.set(laneIds[j], laneY.get(laneIds[j]) - shift);
            }
        }
    }
    return {
        metrics: laneMetrics,
        laneY,
        uniformWidth,
        leftGrowth
    };
}
/**
 * Materialize one lane node against a computed stack. Returns `null` when the
 * node needs no update, so callers can keep the reference stable.
 *
 * `keepStyle` preserves the lane's current `style` (the live drag pass keeps
 * the pre-drag RF node box — see `materializeStack`). When false, the style
 * dimensions participate in the no-op check: a settle whose stack matches the
 * last drag frame must still replace a stale frozen style.
 */ function materializeLaneNode(n, stack, updateLaneData, keepStyle) {
    const m = stack.metrics.get(n.id);
    const y = stack.laneY.get(n.id);
    if (!m || y === undefined) return null;
    const data = n.data;
    // `-0` propagates into later arithmetic; normalize to +0.
    const x = stack.leftGrowth === 0 ? 0 : -stack.leftGrowth;
    const styleMatchesData = keepStyle || n.style?.width === stack.uniformWidth && n.style?.height === m.height;
    if (data.width === stack.uniformWidth && data.height === m.height && (data.upGrowth ?? 0) === m.upGrowth && n.position.y === y && n.position.x === x && styleMatchesData) {
        return null;
    }
    const patched = updateLaneData(n, {
        width: stack.uniformWidth,
        height: m.height,
        upGrowth: m.upGrowth
    });
    const materialized = {
        ...patched,
        position: {
            x,
            y
        }
    };
    return keepStyle ? {
        ...materialized,
        style: n.style
    } : materialized;
}
/**
 * Shared final pass of both layout entry points: materialize every lane
 * against the computed stack and reposition every lane child from its base
 * coordinates (`rf = base + growth` on both axes). Lane origin moves and
 * child compensation land in one atomic array, so a child's absolute X stays
 * glued to its baseX.
 *
 * `frozenStyleLaneIds` keeps the pre-drag RF node box on lanes with a dragged
 * card — resizing the DOM box every frame fires RF's ResizeObserver and
 * stutters the drag. The lane background is SVG driven by `data` and paints
 * with `overflow: visible`, so it grows regardless; the settle pass restores
 * the true box on drop.
 */ function materializeStack(nodes, stack, isLane, updateLaneData, baseById, frozenStyleLaneIds) {
    let changed = false;
    const out = nodes.map((n)=>{
        if (isLane(n)) {
            const lane = materializeLaneNode(n, stack, updateLaneData, frozenStyleLaneIds?.has(n.id) ?? false);
            if (!lane) return n;
            changed = true;
            return lane;
        }
        if (!n.parentId) return n;
        const m = stack.metrics.get(n.parentId);
        if (!m) return n;
        const base = baseById.get(n.id);
        if (base === undefined) return n;
        const newX = base.x + stack.leftGrowth;
        const newY = base.y + m.upGrowth;
        if (newX === n.position.x && newY === n.position.y) return n;
        changed = true;
        return {
            ...n,
            position: {
                x: newX,
                y: newY
            }
        };
    });
    return changed ? out : nodes;
}
/**
 * Pure lane layout: derives each lane's upGrowth, height, width, and Y offset
 * from its children's base coordinates, and returns a new nodes array with
 * lane dimensions and child RF positions updated.
 *
 * Returns the same `nodes` reference when nothing actually changed, so
 * `setNodes(applyLaneLayout(nds, ...))` doesn't trigger spurious renders.
 */ export function applyLaneLayout(nodes, config) {
    const { metrics, isLane, getNodeDimensions, getUpGrowth = defaultGetUpGrowth, updateLaneData = defaultUpdateLaneData } = config;
    const { laneIds, laneById } = collectLanes(nodes, isLane);
    if (laneIds.length === 0) return nodes;
    const children = collectLaneChildren(nodes, laneById, isLane, getNodeDimensions, getUpGrowth);
    const stack = computeLaneStack(laneIds, children, metrics);
    const baseById = new Map();
    for (const c of children)baseById.set(c.id, {
        x: c.baseX,
        y: c.baseY
    });
    return materializeStack(nodes, stack, isLane, updateLaneData, baseById);
}
/**
 * Sub-pixel tolerance for the drag solve — residuals below this are invisible,
 * and react-flow re-derives the dragged cards from the pointer on the next
 * pointermove anyway.
 */ const DRAG_SOLVE_EPSILON = 0.25;
const DRAG_SOLVE_MAX_SWEEPS = 16;
/**
 * Lay out lanes while cards are being dragged, keeping the selection rigid.
 *
 * `dragTargets` holds each dragged card's absolute target position, anchored
 * to the POINTER by the caller (drag-start absolute + pointer delta, plus any
 * snap adjustment the caller's change transform applied this frame — the
 * targets are authoritative, so a snap the caller drops here is a snap the
 * user never sees). We solve
 * for the dragged cards' `baseY`s such that the canonical layout reproduces
 * those targets exactly, then apply that canonical layout atomically: lane
 * origins and dragged-card relative positions change in the same update, so a
 * dragged card never leaves the cursor even for a frame. (Earlier live
 * variants moved lane origins and let react-flow re-pin the card one
 * pointermove later — that one-frame ride is what jittered on upward drags
 * and, with mutually-pushing lanes, diverged.)
 *
 * X needs no iterative solve: a child's absolute X equals its baseX (lane
 * `x = -leftGrowth` cancels the child's `+leftGrowth`), so a dragged card's
 * target X is assigned directly as its baseX before the Y solve, and
 * leftGrowth never feeds back into heights or laneY.
 *
 * The targets MUST come from the pointer, not from the dragged cards' live
 * parent-relative positions: react-flow pins those against its internal
 * nodeLookup, which lags our lane moves by a React commit. For a lane this
 * layout moves every frame, `ourLaneY + rf.y` inherits that lag as a
 * persistent bias — the solve then bakes the bias into the bases, they drift,
 * and a drag-direction reversal lands the system on a degenerate kink where
 * the sweep ratchets without converging (observed blowing lane geometry into
 * the millions mid-drag).
 *
 * Naively committing `baseY + screenDelta` for every dragged card is NOT a
 * solution either: a lane's growth also shifts the lanes above it via the
 * cascade, so a selection spanning lanes would move twice and compress. The
 * solve distributes the deltas so the cascade lands every dragged card on its
 * target. Because the settled/rebuilt layout is the same canonical function of
 * the solved bases, releasing (and reloading) reproduces the drag-end screen
 * state: rigid during drag, no jump on release, rigid at rest.
 */ export function applyLiveDragLaneLayout(nodes, config, dragTargets) {
    if (dragTargets.size === 0) return nodes;
    const { metrics, isLane, getNodeDimensions, getUpGrowth = defaultGetUpGrowth, updateLaneData = defaultUpdateLaneData } = config;
    const { laneIds, laneById } = collectLanes(nodes, isLane);
    if (laneIds.length === 0) return nodes;
    const children = collectLaneChildren(nodes, laneById, isLane, getNodeDimensions, getUpGrowth);
    const draggedByLane = new Map();
    for (const child of children){
        const target = dragTargets.get(child.id);
        if (target === undefined) continue;
        // Absolute X === baseX, so the pointer-anchored target X is the solved
        // baseX directly — no iterative pass needed on this axis.
        child.baseX = target.x;
        let laneDragged = draggedByLane.get(child.laneId);
        if (!laneDragged) {
            laneDragged = [];
            draggedByLane.set(child.laneId, laneDragged);
        }
        laneDragged.push(child);
    }
    if (draggedByLane.size === 0) return nodes;
    // Solve the dragged baseYs. A lane's own bases never move its content anchor
    // (`laneY + upGrowth` — own growth is absorbed by the lane's own origin), so
    // all dragged cards in a lane share one scalar error and adjusting by it
    // lands them exactly; lanes couple only through heights (downward) and
    // growth shifts (upward). That coupling is CHIRAL: an upward drag resolves
    // via lower-lane growth pushing the lanes above (bottom-to-top sweeps
    // converge; top-down ratchets along the layout's degenerate direction), a
    // downward drag resolves via upper-lane height pushing the lanes below
    // (top-down converges; bottom-up ratchets). So solve bottom-up first and, if
    // a residual remains, re-solve top-down from the same start and keep the
    // better result. Each attempt also keeps only its best iterate — on a
    // degenerate constraint set a sweep can walk along the layout's null
    // direction without reducing the residual, and that walk must never render
    // (it once blew lane geometry into the millions).
    const draggedLaneTopDown = laneIds.filter((id)=>draggedByLane.has(id));
    const draggedLaneBottomUp = [
        ...draggedLaneTopDown
    ].reverse();
    const draggedChildren = draggedLaneTopDown.flatMap((id)=>draggedByLane.get(id));
    const initBases = draggedChildren.map((c)=>c.baseY);
    let stack = computeLaneStack(laneIds, children, metrics);
    // Signed miss of a dragged lane's cards under the current stack. All dragged
    // cards in a lane share it (their bases were adjusted in lockstep), so the
    // first card stands in for the lane.
    const laneError = (laneId)=>{
        const laneDragged = draggedByLane.get(laneId);
        const anchor = stack.laneY.get(laneId) + stack.metrics.get(laneId).upGrowth;
        return anchor + laneDragged[0].baseY - dragTargets.get(laneDragged[0].id).y;
    };
    const worstResidual = ()=>{
        let worst = 0;
        for (const laneId of draggedLaneTopDown){
            const err = Math.abs(laneError(laneId));
            if (err > worst) worst = err;
        }
        return worst;
    };
    const runSolve = (order)=>{
        draggedChildren.forEach((c, i)=>{
            c.baseY = initBases[i];
        });
        stack = computeLaneStack(laneIds, children, metrics);
        let best = {
            residual: worstResidual(),
            bases: initBases
        };
        for(let sweep = 0; sweep < DRAG_SOLVE_MAX_SWEEPS; sweep++){
            for (const laneId of order){
                const err = laneError(laneId);
                if (Math.abs(err) <= DRAG_SOLVE_EPSILON) continue;
                for (const c of draggedByLane.get(laneId))c.baseY -= err;
                stack = computeLaneStack(laneIds, children, metrics);
            }
            const residual = worstResidual();
            if (residual < best.residual) {
                best = {
                    residual,
                    bases: draggedChildren.map((c)=>c.baseY)
                };
            }
            if (residual <= DRAG_SOLVE_EPSILON) break;
        }
        return best;
    };
    let solved = runSolve(draggedLaneBottomUp);
    if (solved.residual > DRAG_SOLVE_EPSILON) {
        const topDown = runSolve(draggedLaneTopDown);
        if (topDown.residual < solved.residual) solved = topDown;
    }
    draggedChildren.forEach((c, i)=>{
        c.baseY = solved.bases[i];
    });
    stack = computeLaneStack(laneIds, children, metrics);
    const solvedBase = new Map();
    for (const c of children)solvedBase.set(c.id, {
        x: c.baseX,
        y: c.baseY
    });
    return materializeStack(nodes, stack, isLane, updateLaneData, solvedBase, new Set(draggedByLane.keys()));
}

//# sourceMappingURL=lanes.js.map